import type { AppBadgeColor } from '@shared/ui/badges'
import type { Lesson, LessonTask, LessonTutor } from './interfaces'

import { ActivityStatus, Status as BookingStatus, Type as BookingType } from '@brighterly/lib-core-types/booking/enums'
import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { LessonEntryAction } from './enums'

interface LessonStatusNoteLists {
  reasonCategories: Record<string, string>
  reasons: Record<string, string>
}

const dayjs = useDayjs()

const RESERVED_STATUSES: readonly BookingStatus[] = [BookingStatus.ReservedPlanned, BookingStatus.ReservedConfirmed]

const TYPE_BADGE_COLORS: Partial<Record<BookingType, AppBadgeColor>> = {
  [BookingType.Paid]: 'emerald',
  [BookingType.Demo]: 'indigo',
  [BookingType.PaidDemo]: 'indigo',
}

export const LESSON_EMPTY_CELL = '-'

/** The query key a link to the lesson page carries to open one of its action modals on arrival. */
export const LESSON_ACTION_QUERY = 'action'

/** The cancel, reschedule or delete window a lesson page link asks for; anything else opens the page alone. */
export const getLessonEntryAction = (value: unknown): LessonEntryAction | null =>
  (Object.values(LessonEntryAction).find(action => action === value) ?? null)

export const isReservedLesson = (status: BookingStatus): boolean => RESERVED_STATUSES.includes(status)

export const LESSON_DELETE_RESERVED_HINT = 'You cannot delete a reserved lesson, remove reserved lessons with Cancel reserved'

/** Why Reschedule and Cancel are disabled: the lesson is reserved or completed. */
export const getLessonChangeHint = (status: BookingStatus): string =>
  isReservedLesson(status)
    ? 'You cannot reschedule or cancel a reserved lesson'
    : 'You cannot reschedule or cancel a completed lesson'

export const getLessonTypeBadgeColor = (type: BookingType): AppBadgeColor => TYPE_BADGE_COLORS[type] ?? 'slate'

/** "Oct 21, 2026 15:00 (Wed) (EDT)": the admin's wall clock, then the zone the lesson was booked in. */
export const formatLessonStart = (lesson: Lesson): string => {
  const start = dayjs(lesson.start, DateFormat.Default)
  const zone = lesson.booked_timezone_abbr ? ` (${lesson.booked_timezone_abbr})` : ''

  return `${start.format(DateFormat.KanbanDate)} ${start.format('(ddd)')}${zone}`
}

/** Why the lesson ended up canceled or rescheduled, and where it went: the hint next to the status. */
export const getLessonStatusNote = (lesson: Lesson, lists: LessonStatusNoteLists): string => {
  const parts: string[] = []

  if (lesson.reason) {
    const category = lesson.reason_category ? `${lists.reasonCategories[lesson.reason_category] ?? lesson.reason_category}: ` : ''
    parts.push(`Reason: ${category}${lists.reasons[lesson.reason] ?? lesson.reason}`)
  }

  if (lesson.rescheduled_to) {
    parts.push(`Rescheduled date: ${dayjs(lesson.rescheduled_to.start, DateFormat.Default).format('DD MMM ha')}`)
  }

  return parts.join(' · ')
}

export const formatTutorName = (fullName: string, paidLabel: string | null, paidLabels: Record<string, string>): string => {
  const label = paidLabel ? paidLabels[paidLabel] : null

  return label ? `${fullName} (${label})` : fullName
}

export const getLessonTutorName = (tutor: LessonTutor, paidLabels: Record<string, string>): string =>
  formatTutorName(tutor.full_name, tutor.paid_label, paidLabels)

/** One task reads as its type, several as a count. */
export const getLessonTasksText = (tasks: LessonTask[], typeLabels: Record<string, string>): string => {
  if (!tasks.length) return LESSON_EMPTY_CELL
  if (tasks.length > 1) return `${tasks.length} tasks`

  const [task] = tasks
  return task ? typeLabels[task.type] ?? task.type : LESSON_EMPTY_CELL
}

/** The discipline Tasks tab narrowed to the due dates of the lesson's tasks. */
export const getLessonTasksDueRange = (tasks: LessonTask[]): { due_date_from: string, due_date_to: string } | null => {
  const dueDates = tasks.map(task => task.due_date).filter(Boolean).sort()
  const first = dueDates[0]
  const last = dueDates[dueDates.length - 1]

  return first && last ? { due_date_from: first, due_date_to: last } : null
}

/** The lesson room waits for an admin to let a paid lesson start. */
export const canApproveLessonJoin = (lesson: Lesson): boolean =>
  lesson.activity_status === ActivityStatus.Waiting && lesson.type === BookingType.Paid
